// DelegationDetailSheet — the deep-link target for delegation notifications
// (`/app/admin/delegations?id=<delegationId>`). Opens the one delegation the
// notification is about, for either party, with the handover message and the
// delegate's Accept / Decline when they still have to answer.
//
// A link to something the viewer cannot see (or that no longer exists) says
// so — it never opens an empty drawer.
import React from 'react';
import { Check, XCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/features/shared/components/ui/sheet';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { DataState } from '@/features/shared/components/ui/DataState';
import { DelegationNotes, delegationNotes } from './DelegationNotes';
import { delegationScopeText, fmtWhen, personName, statusVariant } from './DelegateInbox';
import { delegationStatusLabel, firstName, leaveTypeLabel, linkedLeaveStatus, type HandoverDelegation } from './handover';

export type DetailState = 'loading' | 'error' | 'missing' | 'ready';

export interface DelegationDetailProps {
  state: DetailState;
  error?: string | null;
  onRetry?: () => void;
  delegation: HandoverDelegation | null;
  /** True when the viewer is the delegate (vs. the delegator). */
  viewerIsDelegate: boolean;
  /** The manager who ARRANGED this cover (not a party): sees and edits only
   *  their own note (note-editing round 2 — the deep link from their notices). */
  viewerIsArranger?: boolean;
  capLabels: (keys: string[]) => string;
  deciding?: boolean;
  onRespond?: (d: HandoverDelegation, accept: boolean) => void;
  onViewLeave?: (leaveRequestId: string) => void;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className="text-charcoal min-w-0 break-words">{children}</div>
    </div>
  );
}

export const DelegationDetail: React.FC<DelegationDetailProps> = ({
  state, error, onRetry, delegation: d, viewerIsDelegate, viewerIsArranger, capLabels, deciding, onRespond, onViewLeave,
}) => {
  if (state === 'loading') return <DataState variant="loading" message="Loading this delegation…" />;
  if (state === 'error') return <DataState variant="error" title="Couldn't load this delegation" message={error ?? undefined} onRetry={onRetry} />;
  if (state === 'missing' || !d) {
    return (
      <DataState
        variant="unavailable"
        title="Delegation not available"
        message="It may have been withdrawn, or it isn't one of yours. Your current delegations are listed on this page."
      />
    );
  }
  const leave = linkedLeaveStatus(d);
  const from = personName(d.delegatorName);
  return (
    <div className="space-y-4" data-testid="delegation-detail">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={statusVariant(d)}>{delegationStatusLabel(d)}</Badge>
        {leave === 'pending' && <Badge variant="warning">Leave pending approval</Badge>}
        {leave === 'approved' && <Badge variant="success">Leave approved</Badge>}
      </div>
      <div className="space-y-2">
        <Row label="From">{from}</Row>
        <Row label="To">{personName(d.delegateName)}</Row>
        <Row label="Type">{d.leaveRequestId || d.origin === 'leave' ? leaveTypeLabel(d) : 'Delegated authority'}</Row>
        <Row label="Dates">{fmtWhen(d.startsAt)} → {fmtWhen(d.endsAt)}</Row>
        <Row label="Covers">{delegationScopeText(d, capLabels)}</Row>
      </div>
      {viewerIsDelegate && !viewerIsArranger && !delegationNotes(d).employee && !delegationNotes(d).manager ? (
        <div className="text-xs text-muted-foreground">{firstName(d.delegatorName) || from} didn't leave a message.</div>
      ) : (
        <DelegationNotes
          delegation={d}
          viewer={viewerIsDelegate ? 'delegate' : viewerIsArranger ? 'manager' : 'employee'}
          alsoArranger={viewerIsDelegate && viewerIsArranger}
          ownEmptyText={viewerIsArranger ? 'You didn’t leave a note.' : 'You didn’t leave a message.'}
        />
      )}
      {d.status === 'declined' && d.declineReason && <Row label="Declined">{d.declineReason}</Row>}
      <div className="flex flex-wrap gap-2">
        {viewerIsDelegate && d.status === 'pending_acceptance' && onRespond && (
          <>
            <Button size="sm" disabled={deciding} onClick={() => onRespond(d, true)}><Check className="w-4 h-4 mr-1" />Accept</Button>
            <Button size="sm" variant="outline" disabled={deciding} onClick={() => onRespond(d, false)}><XCircle className="w-4 h-4 mr-1" />Decline</Button>
          </>
        )}
        {!viewerIsDelegate && d.leaveRequestId && onViewLeave && (
          <Button size="sm" variant="outline" onClick={() => onViewLeave(d.leaveRequestId!)}>View leave request</Button>
        )}
      </div>
    </div>
  );
};

export interface DelegationDetailSheetProps extends DelegationDetailProps {
  open: boolean;
  onClose: () => void;
}

export const DelegationDetailSheet: React.FC<DelegationDetailSheetProps> = ({ open, onClose, ...detail }) => (
  <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
    <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
      <SheetHeader>
        <SheetTitle>Delegation</SheetTitle>
        <SheetDescription>Who is covering, when, and the handover message.</SheetDescription>
      </SheetHeader>
      <div className="mt-4">
        <DelegationDetail {...detail} />
      </div>
    </SheetContent>
  </Sheet>
);

export default DelegationDetailSheet;
